import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMERGENT_AUTH = "https://auth.emergentagent.com/";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogle() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-6 relative">
      <div className="absolute inset-0 opacity-20 pointer-events-none"
           style={{ background: "radial-gradient(800px 400px at 20% 20%, rgba(255,42,109,0.25), transparent), radial-gradient(600px 400px at 80% 80%, rgba(157,76,221,0.2), transparent)" }} />
      <div className="relative w-full max-w-md bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-8 shadow-2xl">
        <Link to="/" className="relative flex items-center justify-center mb-6" aria-label="TorHub home">
          <span
            aria-hidden="true"
            className="absolute inset-0 -m-6 pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(255,42,109,0.35), rgba(255,42,109,0.12) 55%, transparent 75%)",
              filter: "blur(8px)",
            }}
          />
          <img
            src="https://customer-assets.emergentagent.com/job_torrent-search-1/artifacts/rybfdplq_cooltext510704771142255.png"
            alt="TorHub"
            data-testid="login-logo"
            className="relative h-14 md:h-16 w-auto select-none pointer-events-none drop-shadow-[0_0_18px_rgba(255,42,109,0.55)]"
            draggable={false}
          />
        </Link>
        <h1 className="font-display text-2xl font-bold text-center">Welcome back</h1>
        <p className="text-sm text-[var(--tt-text-muted)] text-center mt-1">Login to track your favorites</p>

        <button
          onClick={startGoogle}
          data-testid="login-google-btn"
          className="w-full mt-6 flex items-center justify-center gap-3 bg-white text-black font-semibold py-2.5 rounded-lg hover:bg-zinc-100 transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.3l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3-11.3-7.4l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.5l6.2 5.2C40 35.9 44 30.5 44 24c0-1.3-.1-2.7-.4-3.9z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-[var(--tt-border)]" />
          <span className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">or</span>
          <div className="flex-1 h-px bg-[var(--tt-border)]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-xs text-[var(--tt-text-muted)] mb-1">Email</div>
            <div className="flex items-center gap-2 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 focus-within:border-[var(--tt-primary)]">
              <Mail className="w-4 h-4 text-[var(--tt-text-muted)]" />
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="flex-1 bg-transparent outline-none text-sm"
                placeholder="you@example.com"
              />
            </div>
          </label>
          <label className="block">
            <div className="text-xs text-[var(--tt-text-muted)] mb-1">Password</div>
            <div className="flex items-center gap-2 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 focus-within:border-[var(--tt-primary)]">
              <Lock className="w-4 h-4 text-[var(--tt-text-muted)]" />
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="flex-1 bg-transparent outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
          </label>
          {err && (
            <div data-testid="login-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Login
          </button>
        </form>

        <div className="text-xs text-[var(--tt-text-muted)] text-center mt-5">
          New here?{" "}
          <Link to="/register" className="text-[var(--tt-primary)] font-semibold hover:underline" data-testid="login-to-register-link">
            Create an account
          </Link>
        </div>
      </div>
    </div>
  );
}
